import { memo, useMemo } from 'react';
import { usePitchField } from './context.js';
import { useSelectionStore } from '@/hooks/useSelection.js';
import { useKeyboardFocusStore } from '@/hooks/useKeyboardFocus.js';
import { useOccupiedStore } from '@/hooks/useOccupied.js';
import { buildUnionPath } from '@/utils/bundlePath.js';

const EMPTY_OBJ = {};

/**
 * Renders a bundle as a single merged SVG <path>.
 *
 * Performance:
 *  - React.memo + per-bundle Zustand selectors (both selection and focus).
 *  - Selection selector uses O(1) object-set lookups.
 *  - Focus selector only triggers a re-render for the two bundles involved
 *    in a focus transition; Grid is unaffected.
 *  - No hover state — CSS :hover handles the visual entirely.
 *    Hover tooltips are rendered by the single HoverTooltip component in Grid.
 *
 * Accessibility:
 *  - role/aria-checked/aria-label are NOT set on the <path> because the SVG
 *    is aria-hidden="true". Screen reader access is handled by the <div tabIndex>
 *    wrapper + FocusedAnnouncement live region in Grid.jsx.
 *
 * Pointer events ensure drag-select works on touch devices.
 */
const BundleTile = memo( function BundleTile( { bundleId, tiles: bundleTiles, onTilePointerDown, onTileDragEnter } ) {
	const { mode, fieldType } = usePitchField();

	const tileIds = useMemo( () => bundleTiles.map( ( t ) => t.id ), [ bundleTiles ] );

	// O(1) per tile — allSelected only true when every bundle tile is in the selection object
	const allSelected = useSelectionStore( ( s ) => {
		const sel = s.selected[ fieldType ];
		if ( ! sel ) return false;
		return tileIds.every( ( id ) => !! sel[ id ] );
	} );

	// Only this bundle re-renders when focus moves to/from it
	const isFocused = useKeyboardFocusStore(
		( s ) => s.hasFocus && s.fieldType === fieldType && s.focusedId === bundleId
	);

	// Only this bundle re-renders when ITS occupied status changes.
	// Prefers the virtual "#bundleId" key; falls back to individual tile entries,
	// returning a 'publish' entry first (so the state calculation is correct).
	const occEntry = useOccupiedStore( ( s ) => {
		const m = s.expandedCache[ fieldType ] ?? EMPTY_OBJ;
		if ( m[ '#' + bundleId ] ) return m[ '#' + bundleId ];
		let pending = null;
		for ( const id of tileIds ) {
			const e = m[ id ];
			if ( ! e ) continue;
			if ( e.status === 'publish' ) return e;
			if ( ! pending ) pending = e;
		}
		return pending;
	} );

	const isOccupied   = !! occEntry;
	const publishedOcc = occEntry?.status === 'publish' ? occEntry : null;

	let state = 'free';
	if ( isOccupied )       state = publishedOcc ? 'occupied' : 'pending';
	else if ( allSelected ) state = 'selected';

	const pathD = useMemo( () => buildUnionPath( bundleTiles ), [ bundleTiles ] );

	const { cx, cy, markerSize } = useMemo( () => {
		const xs = bundleTiles.flatMap( ( t ) => [ t.x, t.x + t.w ] );
		const ys = bundleTiles.flatMap( ( t ) => [ t.y, t.y + t.h ] );
		return {
			cx:         ( Math.min( ...xs ) + Math.max( ...xs ) ) / 2,
			cy:         ( Math.min( ...ys ) + Math.max( ...ys ) ) / 2,
			markerSize: Math.min( bundleTiles[ 0 ]?.w ?? 2, bundleTiles[ 0 ]?.h ?? 2 ) * 0.7,
		};
	}, [ bundleTiles ] );

	function handlePointerDown( e ) {
		// Ignore non-primary mouse buttons; allow all touch/stylus
		if ( e.pointerType === 'mouse' && e.button !== 0 ) return;
		// Prevent scroll on touch/stylus during drag-select
		if ( e.pointerType !== 'mouse' ) e.preventDefault();
		// Release implicit pointer capture so pointerenter fires on other tiles during drag
		e.target.releasePointerCapture( e.pointerId );
		onTilePointerDown?.( bundleId, tileIds, isOccupied );
	}

	return (
		<g
			data-nav-id={bundleId}
			className={`wsp-bundle-tile wsp-bundle-tile--${ state }${ isFocused ? ' wsp-bundle-tile--keyboard-focus' : '' }`}
			style={bundleTiles[ 0 ]?.customStyle ?? undefined}
			onPointerEnter={() => onTileDragEnter?.( bundleId, tileIds, isOccupied )}
		>
			<path
				d={pathD}
				className="wsp-bundle-tile__path"
				onPointerDown={handlePointerDown}
			/>

			{isFocused && (
				<text
					x={cx}
					y={cy}
					className="wsp-keyboard-cursor"
					textAnchor="middle"
					dominantBaseline="central"
					fontSize={markerSize}
					aria-hidden="true"
				>
					◎
				</text>
			)}
		</g>
	);
} );

export default BundleTile;
